import type {
  AdminListAuditResponse,
  AdminListUsersResponse,
  AdminModerateDeckRequest,
  AdminSetDisabledRequest,
  AdminSetRoleRequest,
  AdminStatsResponse,
  User,
  UserRole,
  UserStatus,
} from '@study-decks/shared'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') ?? ''

export class AdminApiError extends Error {
  override readonly name = 'AdminApiError'

  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message)
  }
}

type TokenProvider = () => Promise<string>

interface RequestOptions extends RequestInit {
  getIdToken: TokenProvider
}

interface ListUsersParams {
  search?: string
  role?: UserRole | 'all'
  status?: UserStatus | 'all'
  pageSize?: number
  pageToken?: string | null
}

interface ListAuditParams {
  pageSize?: number
  pageToken?: string | null
}

async function requestAdminApi<T>(path: string, { getIdToken, headers, ...init }: RequestOptions) {
  const idToken = await getIdToken()
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      authorization: `Bearer ${idToken}`,
      ...headers,
    },
  })

  if (!response.ok) {
    const errorBody = (await response.json().catch(() => ({}))) as {
      error?: { code?: string; message?: string }
    }
    throw new AdminApiError(
      errorBody.error?.code ?? 'REQUEST_FAILED',
      errorBody.error?.message ?? `Request failed (${response.status})`,
    )
  }

  if (response.status === 204) {
    return null as T
  }

  return (await response.json()) as T
}

export function listAdminUsers(getIdToken: TokenProvider, params: ListUsersParams) {
  const query = new URLSearchParams()
  if (params.search?.trim()) query.set('search', params.search.trim())
  if (params.role && params.role !== 'all') query.set('role', params.role)
  if (params.status && params.status !== 'all') query.set('status', params.status)
  if (params.pageSize) query.set('pageSize', String(params.pageSize))
  if (params.pageToken) query.set('pageToken', params.pageToken)

  const suffix = query.toString() ? `?${query.toString()}` : ''
  return requestAdminApi<AdminListUsersResponse>(`/api/admin/users${suffix}`, {
    method: 'GET',
    getIdToken,
  })
}

export function setAdminUserRole(
  getIdToken: TokenProvider,
  uid: string,
  body: AdminSetRoleRequest,
) {
  return requestAdminApi<User>(`/api/admin/users/${uid}/role`, {
    method: 'POST',
    getIdToken,
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify(body),
  })
}

export function setAdminUserDisabled(
  getIdToken: TokenProvider,
  uid: string,
  body: AdminSetDisabledRequest,
) {
  return requestAdminApi<User>(`/api/admin/users/${uid}/disabled`, {
    method: 'POST',
    getIdToken,
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify(body),
  })
}

export function listAdminAudit(getIdToken: TokenProvider, params: ListAuditParams) {
  const query = new URLSearchParams()
  if (params.pageSize) query.set('pageSize', String(params.pageSize))
  if (params.pageToken) query.set('pageToken', params.pageToken)

  const suffix = query.toString() ? `?${query.toString()}` : ''
  return requestAdminApi<AdminListAuditResponse>(`/api/admin/audit${suffix}`, {
    method: 'GET',
    getIdToken,
  })
}

export function moderateDeck(
  getIdToken: TokenProvider,
  deckId: string,
  body: AdminModerateDeckRequest,
) {
  return requestAdminApi<Record<string, unknown>>(`/api/admin/decks/${deckId}/moderate`, {
    method: 'POST',
    getIdToken,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
}

export function getAdminStats(getIdToken: TokenProvider) {
  return requestAdminApi<{ stats: AdminStatsResponse; cached: boolean }>('/api/admin/stats', {
    method: 'GET',
    getIdToken,
  })
}
